import { Client, Account, Databases, Query } from 'appwrite'
import { PersistenceProvider, QueryBuilder } from './provider.interface.js'
import { config } from '../config.js'

/**
 * Appwrite 持久化提供商实现
 */
export class AppwriteProvider extends PersistenceProvider {
  constructor() {
    super()

    const endpoint = config.APPWRITE_URL || 'http://localhost/v1'
    const projectId = config.APPWRITE_PROJECT_ID || 'your-project-id'
    const databaseId = config.APPWRITE_DATABASE_ID || 'default'

    this.client = new Client()
      .setEndpoint(endpoint)
      .setProject(projectId)

    this.account = new Account(this.client)
    this.databases = new Databases(this.client)
    this.databaseId = databaseId

    // 用于存储当前用户会话
    this.currentSession = null
    this.authStateCallbacks = []
    
    // 初始化会话
    this.initializeSession()
  }

  normalizeUser(user, fallbackUsername) {
    if (!user) {
      return null
    }

    const normalized = {
      ...user,
      id: user.id || user.$id || null
    }

    const metadata = {
      ...(user.user_metadata || {})
    }

    if (!metadata.username) {
      metadata.username = user.username || user.name || fallbackUsername || (user.email ? user.email.split('@')[0] : undefined)
    }

    normalized.user_metadata = metadata

    if (!normalized.username && metadata.username) {
      normalized.username = metadata.username
    }

    return normalized
  }

  async initializeSession() {
    try {
      const user = await this.account.get()
      const normalizedUser = this.normalizeUser(user)
      this.currentSession = { user: normalizedUser }
      this.notifyAuthStateChange('INITIAL_SESSION', { user: normalizedUser })
    } catch (error) {
      this.currentSession = null
    }
  }

  async signUp(email, password, metadata = {}) {
    try {
      const user = await this.account.create('unique()', email, password)
      
      // 在用户集合中创建用户配置文件
      // 注意：$id, $createdAt, $updatedAt 由 Appwrite 自动生成，不需要手动设置
      try {
        await this.databases.createDocument(
          this.databaseId,
          'users',
          user.$id,
          {
            email: user.email,
            username: metadata.username || email.split('@')[0]
          }
        )
      } catch (profileError) {
        console.warn('Failed to create user profile:', profileError)
      }

      const normalizedUser = this.normalizeUser(user, metadata.username)

      return { data: { user: normalizedUser }, error: null }
    } catch (error) {
      return { data: null, error: error }
    }
  }

  async signIn(email, password) {
    try {
      // 首先清除现有会话
      try {
        await this.account.deleteSession('current')
      } catch (e) {
        // 忽略删除失败的错误
      }

      // 创建新会话（兼容不同 SDK 版本）
      let session
      if (typeof this.account.createEmailPasswordSession === 'function') {
        session = await this.account.createEmailPasswordSession(email, password)
      } else if (typeof this.account.createEmailSession === 'function') {
        session = await this.account.createEmailSession(email, password)
      } else {
        throw new Error('Email/password session API is not available in the Appwrite SDK')
      }
      const user = await this.account.get()
      const normalizedUser = this.normalizeUser(user)
      const normalizedSession = session ? { ...session } : null

      this.currentSession = { user: normalizedUser, session: normalizedSession }
      this.notifyAuthStateChange('SIGNED_IN', { user: normalizedUser, session: normalizedSession })

      return { data: { user: normalizedUser, session: normalizedSession }, error: null }
    } catch (error) {
      return { data: null, error: error }
    }
  }

  async signOut() {
    try {
      await this.account.deleteSession('current')
      this.currentSession = null
      this.notifyAuthStateChange('SIGNED_OUT', null)
      return { error: null }
    } catch (error) {
      return { error: error }
    }
  }

  async getSession() {
    try {
      const session = await this.account.getSession('current')
      const user = await this.account.get()
      const normalizedUser = this.normalizeUser(user)
      const normalizedSession = session ? { ...session } : null
      this.currentSession = { user: normalizedUser, session: normalizedSession }
      return { data: { session: this.currentSession }, error: null }
    } catch (error) {
      this.currentSession = null
      return { data: { session: null }, error: null }
    }
  }

  async getUser() {
    try {
      const user = await this.account.get()
      const normalizedUser = this.normalizeUser(user)
      return { data: { user: normalizedUser }, error: null }
    } catch (error) {
      return { data: { user: null }, error: error }
    }
  }

  onAuthStateChange(callback) {
    this.authStateCallbacks.push(callback)

    // 返回订阅对象（兼容 Supabase 接口）
    return {
      unsubscribe: () => {
        this.authStateCallbacks = this.authStateCallbacks.filter(cb => cb !== callback)
      }
    }
  }

  notifyAuthStateChange(event, session) {
    const normalizedUser = session?.user ? this.normalizeUser(session.user) : null
    const normalizedSession = session ? { ...session, user: normalizedUser } : null

    this.authStateCallbacks.forEach(callback => {
      callback(event, normalizedUser, normalizedSession)
    })
  }

  async ensureUserProfile(user, username) {
    try {
      // 检查用户配置文件是否存在
      const userId = user.$id || user.id
      const userDoc = await this.databases.getDocument(
        this.databaseId,
        'users',
        userId
      )
      return
    } catch (error) {
      // 用户配置文件不存在，创建它
      // 注意：$id, $createdAt, $updatedAt 由 Appwrite 自动生成，不需要手动设置
      try {
        const userId = user.$id || user.id
        await this.databases.createDocument(
          this.databaseId,
          'users',
          userId,
          {
            email: user.email,
            username: user.username || username
          }
        )
      } catch (createError) {
        console.error('Failed to create user profile:', createError)
      }
    }
  }

  from(collection) {
    return new AppwriteQueryBuilder(
      this.databases,
      this.databaseId,
      collection,
      this.account
    )
  }

  getDatabase() {
    return this.databases
  }
}

/**
 * Appwrite 查询构建器
 */
class AppwriteQueryBuilder extends QueryBuilder {
  constructor(databases, databaseId, collection, account) {
    super()
    this.databases = databases
    this.databaseId = databaseId
    this.collection = collection
    this.account = account
    this.filters = []
    this.orderBy = null
    this.selectFields = []
  }

  mapAttribute(attribute) {
    switch (attribute) {
      case 'id':
        return '$id'
      case 'created_at':
        return '$createdAt'
      case 'updated_at':
        return '$updatedAt'
      default:
        return attribute
    }
  }

  transformDocument(doc) {
    if (!doc) return doc

    const transformed = { ...doc }

    if (transformed.$id && (transformed.id === undefined || transformed.id === null)) {
      transformed.id = transformed.$id
    }

    if (transformed.$createdAt && (transformed.created_at === undefined || transformed.created_at === null)) {
      transformed.created_at = transformed.$createdAt
    } else if (transformed.createdAt && (transformed.created_at === undefined || transformed.created_at === null)) {
      transformed.created_at = transformed.createdAt
    }

    if (transformed.$updatedAt && (transformed.updated_at === undefined || transformed.updated_at === null)) {
      transformed.updated_at = transformed.$updatedAt
    } else if (transformed.updatedAt && (transformed.updated_at === undefined || transformed.updated_at === null)) {
      transformed.updated_at = transformed.updatedAt
    }

    return transformed
  }

  select(fields = '*') {
    this.selectFields = fields === '*' ? [] : fields.split(',').map(f => f.trim())
    return this
  }

  async insert(data) {
    try {
      const result = await this.databases.createDocument(
        this.databaseId,
        this.collection,
        'unique()',
        data
      )
      return { data: this.transformDocument(result), error: null }
    } catch (error) {
      return { data: null, error: error }
    }
  }

  update(data) {
    this.updateData = data
    // 返回一个具有更新执行功能的代理对象，同时保持链式调用
    const proxy = {
      eq: (column, value) => {
        this.eq(column, value)
        return proxy
      },
      in: (column, values) => {
        this.in(column, values)
        return proxy
      },
      updateExec: () => this.updateExec(),
      then: (resolve) => this.updateExec().then(resolve)
    }
    
    // 保存原始的 this 指针
    Object.setPrototypeOf(proxy, this)
    
    return proxy
  }

  async delete() {
    try {
      // 获取所有匹配的文档
      const docs = await this.databases.listDocuments(
        this.databaseId,
        this.collection,
        this.filters
      )

      // 删除所有匹配的文档
      for (const doc of docs.documents) {
        await this.databases.deleteDocument(
          this.databaseId,
          this.collection,
          doc.$id
        )
      }

      return { error: null }
    } catch (error) {
      return { error: error }
    }
  }

  eq(column, value) {
    const attribute = this.mapAttribute(column)
    this.filters.push(Query.equal(attribute, value))
    return this
  }

  in(column, values) {
    const attribute = this.mapAttribute(column)
    this.filters.push(Query.equal(attribute, values))
    return this
  }

  order(column, options = {}) {
    this.orderBy = {
      column,
      mappedColumn: this.mapAttribute(column),
      ascending: options.ascending !== false
    }
    return this
  }

  async single() {
    try {
      const result = await this.exec()
      if (result.data && result.data.length > 0) {
        return { data: result.data[0], error: null }
      }
      return { data: null, error: null }
    } catch (error) {
      return { data: null, error: error }
    }
  }

  async exec() {
    try {
      let result = await this.databases.listDocuments(
        this.databaseId,
        this.collection,
        this.filters
      )

      let documents = result.documents

      // 处理排序
      if (this.orderBy) {
        const orderKey = this.orderBy.mappedColumn
        const fallbackKey = this.orderBy.column

        documents.sort((a, b) => {
          const aVal = a[orderKey] ?? a[fallbackKey]
          const bVal = b[orderKey] ?? b[fallbackKey]

          if (aVal === bVal) return 0

          if (this.orderBy.ascending) {
            return aVal > bVal ? 1 : -1
          } else {
            return aVal < bVal ? 1 : -1
          }
        })
      }

      // 处理字段选择
      if (this.selectFields.length > 0) {
        documents = documents.map(doc => {
          const selected = {}
          this.selectFields.forEach(field => {
            selected[field] = doc[field]
          })
          if (doc.$id && selected.id === undefined) {
            selected.id = doc.$id
          }
          if (doc.$createdAt && selected.created_at === undefined) {
            selected.created_at = doc.$createdAt
          }
          return selected
        })
      }

      documents = documents.map(doc => this.transformDocument(doc))

      return { data: documents, error: null }
    } catch (error) {
      return { data: null, error: error }
    }
  }

  // 处理更新操作
  async updateExec() {
    try {
      const docs = await this.databases.listDocuments(
        this.databaseId,
        this.collection,
        this.filters
      )

      const results = []
      for (const doc of docs.documents) {
        const result = await this.databases.updateDocument(
          this.databaseId,
          this.collection,
          doc.$id,
          this.updateData
        )
        results.push(this.transformDocument(result))
      }

      return { data: results, error: null }
    } catch (error) {
      return { data: null, error: error }
    }
  }
}

export const appwriteConfig = {
  endpoint: config.APPWRITE_URL,
  projectId: config.APPWRITE_PROJECT_ID,
  databaseId: config.APPWRITE_DATABASE_ID
}
